import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError, formatMoney, todayLocalIso } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Receipt, FileSpreadsheet, FileText, ExternalLink, Wallet } from "lucide-react";
import { exportToExcel, exportToPdf } from "@/lib/exportUtils";

export default function CxCFacturada() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const today = todayLocalIso();
  const firstDayMonth = today.slice(0, 8) + "01";
  const [range, setRange] = useState({ desde: firstDayMonth, hasta: today });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/reports/cxc-facturada?desde=${range.desde}&hasta=${range.hasta}`);
      setItems(data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Agrupación por caja o banco (para el sub-reporte "Facturado en Caja")
  const porDestino = useMemo(() => {
    const map = new Map();
    for (const o of items) {
      for (const p of (o.pagos || [])) {
        const key = p.caja_id ? `caja::${p.caja_id}` : `banco::${(p.banco || "—").trim()}`;
        const label = p.caja_id ? (p.caja_nombre || `Caja ${p.caja_id.slice(-6)}`) : (p.banco || "—");
        if (!map.has(key)) map.set(key, { key, label, tipo: p.caja_id ? "Caja" : "Banco", total: 0, pedidos: new Set(), pagos: [] });
        const g = map.get(key);
        g.total += Number(p.monto || 0);
        g.pedidos.add(o.id);
        g.pagos.push({ order_id: o.id, numero: o.numero, cliente: o.cliente, monto: p.monto, metodo: p.metodo });
      }
    }
    return Array.from(map.values()).map(g => ({ ...g, pedidos_count: g.pedidos.size })).sort((a, b) => b.total - a.total);
  }, [items]);

  const [openGroup, setOpenGroup] = useState(null); // key to show pedidos list

  // Preparar rows para exportar (flatten pagos)
  const flatRows = items.flatMap(o => {
    if ((o.pagos || []).length === 0) {
      return [{
        numero: o.numero, fecha_ingreso: o.fecha_ingreso, mes_origen: o.mes_origen,
        vendedor: o.vendedor_nombre || o.vendedor, cliente: o.cliente,
        productos: (o.productos || []).join(", "), total_pedido: o.total_pedido,
        total_facturado: o.total_facturado, total_pagado: o.total_pagado, saldo: o.saldo,
        banco_o_caja: "", forma_pago: "", num_factura: (o.facturas?.[0]?.numero || ""), fecha_factura: (o.facturas?.[0]?.created_at || "").slice(0,10),
      }];
    }
    return o.pagos.map(p => ({
      numero: o.numero, fecha_ingreso: o.fecha_ingreso, mes_origen: o.mes_origen,
      vendedor: o.vendedor_nombre || o.vendedor, cliente: o.cliente,
      productos: (o.productos || []).join(", "), total_pedido: o.total_pedido,
      total_facturado: o.total_facturado, total_pagado: p.monto, saldo: o.saldo,
      banco_o_caja: p.caja_id ? `Caja ${p.caja_id.slice(-6)}` : (p.banco || ""),
      forma_pago: p.metodo || "", num_factura: (o.facturas?.[0]?.numero || ""), fecha_factura: (o.facturas?.[0]?.created_at || "").slice(0,10),
    }));
  });

  const columns = [
    { key: "numero", label: "N° Pedido", width: 14 },
    { key: "fecha_ingreso", label: "Fecha ingreso", type: "date", width: 14 },
    { key: "mes_origen", label: "Mes origen", width: 12 },
    { key: "fecha_factura", label: "Fecha factura", type: "date", width: 14 },
    { key: "num_factura", label: "N° Factura", width: 14 },
    { key: "vendedor", label: "Vendedor", width: 18 },
    { key: "cliente", label: "Cliente", width: 24 },
    { key: "productos", label: "Productos", width: 32 },
    { key: "total_pedido", label: "Total pedido", type: "money", width: 14 },
    { key: "total_facturado", label: "Facturado", type: "money", width: 14 },
    { key: "total_pagado", label: "Pagado", type: "money", width: 14 },
    { key: "saldo", label: "Saldo", type: "money", width: 14 },
    { key: "banco_o_caja", label: "Banco / Caja", width: 18 },
    { key: "forma_pago", label: "Forma pago", width: 14 },
  ];

  const doExport = (fmt) => {
    const filters = { "Periodo": `${range.desde} → ${range.hasta}`, "Registros": items.length };
    const meta = { title: "CxC Facturada — Pedidos meses anteriores facturados en el periodo",
                     filters, columns, rows: flatRows, filename: `cxc_facturada_${range.desde}_${range.hasta}` };
    fmt === "xlsx" ? exportToExcel(meta) : exportToPdf({ ...meta, landscape: true });
  };

  return (
    <div className="space-y-6" data-testid="cxc-page">
      <header>
        <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 font-semibold">Reportes financieros</div>
        <h1 className="font-manrope font-black tracking-tight text-4xl mt-1 flex items-center gap-3">
          <Receipt className="w-8 h-8" /> CxC Facturada
        </h1>
        <p className="text-zinc-500 mt-2 text-sm">Pedidos ingresados en meses anteriores que fueron facturados durante el periodo seleccionado. Muestra dónde entró el dinero (banco o caja).</p>
      </header>

      <Card className="p-4 border-zinc-200 rounded-md">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label>Desde</Label>
            <Input type="date" value={range.desde} onChange={(e) => setRange({ ...range, desde: e.target.value })} className="w-40" />
          </div>
          <div>
            <Label>Hasta</Label>
            <Input type="date" value={range.hasta} onChange={(e) => setRange({ ...range, hasta: e.target.value })} className="w-40" />
          </div>
          <Button onClick={load} className="bg-[#002FA7] hover:bg-[#00227A]" data-testid="cxc-buscar">Buscar</Button>
          <div className="flex-1"></div>
          <Button variant="outline" onClick={() => doExport("xlsx")} className="gap-2" data-testid="cxc-xlsx">
            <FileSpreadsheet className="w-4 h-4" /> Excel
          </Button>
          <Button variant="outline" onClick={() => doExport("pdf")} className="gap-2" data-testid="cxc-pdf">
            <FileText className="w-4 h-4" /> PDF
          </Button>
        </div>
      </Card>

      <Card className="border-zinc-200 rounded-md overflow-hidden" data-testid="cxc-por-destino">
        <div className="p-4 border-b border-zinc-100">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Facturado por caja / banco</div>
          <div className="text-lg font-semibold mt-0.5">Distribución del dinero cobrado</div>
        </div>
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {porDestino.length === 0 && (
            <div className="col-span-full text-zinc-400 text-sm text-center py-4">Sin pagos en el periodo.</div>
          )}
          {porDestino.map((g) => (
            <div key={g.key} className={`rounded-md border p-4 ${g.tipo === "Caja" ? "border-amber-200 bg-amber-50" : "border-zinc-200 bg-white"}`} data-testid={`grupo-${g.key}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  {g.tipo === "Caja" ? <Wallet className="w-4 h-4 text-amber-700 shrink-0" /> : <Receipt className="w-4 h-4 text-zinc-600 shrink-0" />}
                  <span className="text-[10px] uppercase tracking-widest font-semibold text-zinc-500">{g.tipo}</span>
                </div>
                <button
                  onClick={() => setOpenGroup(openGroup === g.key ? null : g.key)}
                  className="text-[11px] text-[#002FA7] hover:underline"
                  data-testid={`ver-pedidos-${g.key}`}
                >
                  Ver pedidos ({g.pedidos_count})
                </button>
              </div>
              <div className="mt-1 font-semibold truncate">{g.label}</div>
              <div className="mt-2 font-manrope font-black text-2xl tabular-nums text-blue-700">{formatMoney(g.total)}</div>
              {openGroup === g.key && (
                <div className="mt-3 pt-3 border-t border-zinc-200 space-y-1 max-h-56 overflow-y-auto">
                  {g.pagos.map((p, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 text-xs">
                      <Link to={`/pedidos/${p.order_id}`} className="font-semibold text-[#002FA7] hover:underline flex items-center gap-1">
                        {p.numero} <ExternalLink className="w-3 h-3" />
                      </Link>
                      <span className="text-zinc-500 truncate flex-1">{p.cliente}</span>
                      <span className="font-semibold tabular-nums">{formatMoney(p.monto)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>

      <Card className="border-zinc-200 rounded-md overflow-hidden">
        <div className="p-4 border-b border-zinc-100 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Resultados</div>
            <div className="font-semibold text-xl">{items.length} pedidos</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Total facturado</div>
            <div className="font-manrope font-black text-2xl text-blue-700 tabular-nums">
              {formatMoney(items.reduce((s, x) => s + Number(x.total_facturado || 0), 0))}
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-600">
              <tr className="text-left">
                <th className="px-3 py-2 text-[10px] uppercase font-semibold">Pedido</th>
                <th className="px-3 py-2 text-[10px] uppercase font-semibold">Ingreso</th>
                <th className="px-3 py-2 text-[10px] uppercase font-semibold">Mes origen</th>
                <th className="px-3 py-2 text-[10px] uppercase font-semibold">Cliente</th>
                <th className="px-3 py-2 text-[10px] uppercase font-semibold">Facturas</th>
                <th className="px-3 py-2 text-[10px] uppercase font-semibold">Pagos (banco/caja)</th>
                <th className="px-3 py-2 text-[10px] uppercase font-semibold text-right">Facturado</th>
                <th className="px-3 py-2 text-[10px] uppercase font-semibold text-right">Pagado</th>
                <th className="px-3 py-2 text-[10px] uppercase font-semibold text-right">Saldo</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {loading && <tr><td colSpan={9} className="text-center text-zinc-400 py-8">Cargando…</td></tr>}
              {!loading && items.length === 0 && <tr><td colSpan={9} className="text-center text-zinc-400 py-10">Sin pedidos que cumplan los criterios.</td></tr>}
              {items.map((o) => (
                <tr key={o.id} className="hover:bg-zinc-50">
                  <td className="px-3 py-2 font-mono text-xs font-semibold">
                    <Link to={`/pedidos/${o.id}`} className="text-[#002FA7] hover:underline">{o.numero}</Link>
                  </td>
                  <td className="px-3 py-2 text-xs">{o.fecha_ingreso}</td>
                  <td className="px-3 py-2 text-xs">{o.mes_origen}</td>
                  <td className="px-3 py-2 text-xs">{o.cliente}</td>
                  <td className="px-3 py-2 text-xs">
                    {(o.facturas || []).map((f, i) => (
                      <div key={i}>{f.numero} · {(f.created_at || "").slice(0, 10)}</div>
                    ))}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {(o.pagos || []).map((p, i) => (
                      <div key={i} className="text-zinc-700">Q{Number(p.monto).toFixed(2)} → <b>{p.caja_id ? "Caja" : (p.banco || "—")}</b> {p.metodo ? `(${p.metodo})` : ""}</div>
                    ))}
                    {(o.pagos || []).length === 0 && <span className="text-zinc-400">Sin pagos</span>}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold text-blue-700">{formatMoney(o.total_facturado)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold text-emerald-700">{formatMoney(o.total_pagado)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold text-orange-700">{formatMoney(o.saldo)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
